

import { ProviderCustomActionType } from '@/domain/enums/provider-custom-action-type';
import { IncentiveStep } from 'interfaces';
import {
	IIncentiveV2Actions,
	IIncentiveV2Getters,
	IIncentiveV2Mutations,
} from '@/domain/dto/store.incentive-v2.interfaces';
import { IAuthenticationState, IncentiveAuthenticationInput } from '@/domain/dto/student-authentication-state.interface';
import { StudentAuthenticationService } from '@/domain/services/student-authentication-service';
import { ICaptchaInstance } from '@/domain/services/captcha-service';
import { SignInFlow } from '@/infrastructure/clients/studentapi/enums/sign-in-flow';
import { ErrorDisplayService } from '@/domain/services/error-display-service';
import IEmailPreference from '@/domain/dto/email-preference.interface';
import globals from '@/utils/globals';
import { OptInEvent } from '@/utils/tracking/enums/optin-event.enum';
import { trackIncentiveOptIn, trackIncentiveRegistration, trackIncentiveLogin, trackIncentiveAction, getPageType } from '@/utils/tracking/incentive-tracking';
import { AuthenticationProviderType } from '@/domain/enums/authentication-provider-type';
import Labels from '@/dictionary/tracking/labels';

const errorDisplayService = new ErrorDisplayService();

export class IncentiveV2Actions implements IIncentiveV2Actions {
	public constructor(
		private readonly mutations: IIncentiveV2Mutations,
		private readonly getters: IIncentiveV2Getters,
		private readonly captcha: ICaptchaInstance,
		private readonly studentAuthenticationService: StudentAuthenticationService,
		private readonly steps: IncentiveStep[] = [IncentiveStep.REGISTRATION, IncentiveStep.OPT_IN, IncentiveStep.CPI],
		private readonly onFinished?: () => void,
	) {}


	public saveName(name: string): void {
		this.mutations.setName(name);
	}

	public saveEmail(email: string): void {
		this.mutations.setEmail(email);
	}

	public savePassword(password: string): void {
		this.mutations.setPassword(password);
	}

	public saveConfirmPassword(password: string): void {
		this.mutations.setConfirmPassword(password);
	}
	public moveToNextStep(): void {
		const nextStep = this.steps[this.steps.indexOf(this.getters.step()) + 1] ?? this.getters.step();
		this.mutations.setStep(nextStep);
	}

	private async authenticate(
		input: IncentiveAuthenticationInput,
		directAuthType: ProviderCustomActionType,
		signInFlow: SignInFlow): Promise<void> {
		if (input.provider === AuthenticationProviderType.DIRECT) {
			if (!input.email || !input.password || (directAuthType === ProviderCustomActionType.REGISTER && !input.captcha)) {
				this.mutations.setErrorMessage({ message: 'Please fill in all required fields.', type: null });
				return;
			}
		}
		if (input.provider === AuthenticationProviderType.GOOGLE && !input.googleIdToken) {
			this.mutations.setErrorMessage({ message: 'Google authentication is incomplete.', type: null });
			return;
		}
		if (input.provider === AuthenticationProviderType.FACEBOOK && !input.facebookAccessToken) {
			this.mutations.setErrorMessage({ message: 'Facebook authentication is incomplete.', type: null });
			return;
		}

		this.mutations.setErrorMessage({ message: null, type: null });
		this.mutations.setPending(true);

		try {
			const state = this.buildState(input);
			trackIncentiveAction(
				Labels.AUTH_STARTED,
				this.getters.incentiveType(),
				this.getters.location(),
				this.getters.uniqueIdentifier(),
			);
			const result = await this.studentAuthenticationService.authenticate(
				state,
				input.provider,
				directAuthType,
				{
					registration_incentive_location: this.getters.location(),
					registration_incentive: this.getters.incentiveType(),
					registration_incentive_identifier: this.getters.uniqueIdentifier(),
					registration_incentive_page_type: getPageType()
				}
			);
			trackIncentiveAction(
				Labels.AUTH_COMPLETED,
				this.getters.incentiveType(),
				this.getters.location(),
				this.getters.uniqueIdentifier(),
			);

			this.trackAuthenticationResult(result.flow, input.provider);

			if (result.flow === signInFlow) {
				this.mutations.setAccessToken(result.credentials.accessToken);
				this.setPostAuthenticationStep(
					directAuthType === ProviderCustomActionType.REGISTER
						? IncentiveStep.REGISTRATION
						: IncentiveStep.LOGIN,
				);
			}
			/* LOGIN/CONVERTED: the student already existed, stay on REGISTRATION (see
				docs/incentive-flow-implementation-plan.md, IncentiveStep transitions) */
		} catch (error) {
			this.mutations.setErrorMessage({ message: errorDisplayService.errorMessage(error as Error), type: null });
			globals.eventBus.emit('resetCaptcha');
		} finally {
			this.mutations.setPending(false);
		}
	}

	public async register(input: IncentiveAuthenticationInput): Promise<void> {
		await this.authenticate(input, ProviderCustomActionType.REGISTER, SignInFlow.REGISTERED);
	}



	public async login(input: IncentiveAuthenticationInput): Promise<void> {
		await this.authenticate(input, ProviderCustomActionType.LOGIN, SignInFlow.LOGIN);
	}

	public async forgotPassword(): Promise<void> {
		if (!this.getters.isValidEmail()) {
			this.mutations.setErrorMessage({ message: 'Please fill in all required fields.', type: null });
			return;
		}

		this.mutations.setErrorMessage({ message: null, type: null });
		this.mutations.setPending(true);

		try {
			trackIncentiveAction(
				Labels.PASSWORD_RESET_STARTED,
				this.getters.incentiveType(),
				this.getters.location(),
				this.getters.uniqueIdentifier(),
			);
			await this.studentAuthenticationService.forgotPassword(this.buildState());
			this.mutations.setResetPasswordEmailSent(true);
			trackIncentiveAction(
				Labels.PASSWORD_RESET_COMPLETED,
				this.getters.incentiveType(),
				this.getters.location(),
				this.getters.uniqueIdentifier(),
			);
		} catch (error) {
			this.mutations.setErrorMessage({ message: errorDisplayService.errorMessage(error as Error), type: null });
		} finally {
			this.mutations.setPending(false);
		}
	}



	public async acceptOptIn(preferences?: IEmailPreference): Promise<void> {
		const state = this.buildState();
		try {
			await this.studentAuthenticationService.subscribeForEmails(state, preferences);
		} catch (error) {
			/* registration already succeeded; still advance rather than trap the student here */
			this.mutations.setErrorMessage({ message: errorDisplayService.errorMessage(error as Error), type: null });
		}
		trackIncentiveOptIn(
			OptInEvent.ACCEPT,
			this.getters.incentiveType(),
			this.getters.location(),
			this.getters.uniqueIdentifier(),
		);
		this.setNextStep(IncentiveStep.OPT_IN, IncentiveStep.CPI);
	}

	public denyOptIn(): Promise<void> {
		trackIncentiveOptIn(
			OptInEvent.DENY,
			this.getters.incentiveType(),
			this.getters.location(),
			this.getters.uniqueIdentifier(),
		);
		this.setNextStep(IncentiveStep.OPT_IN, IncentiveStep.CPI);
		return Promise.resolve();
	}

	public dismissOptIn(): Promise<void> {
		trackIncentiveOptIn(
			OptInEvent.DISMISS,
			this.getters.incentiveType(),
			this.getters.location(),
			this.getters.uniqueIdentifier(),
		);
		this.setNextStep(IncentiveStep.OPT_IN, IncentiveStep.CPI);
		return Promise.resolve();
	}

	public setNextStep(current: IncentiveStep, fallback: IncentiveStep): void {
		const next = this.steps
			.slice(this.steps.indexOf(current) + 1)
			.find((step) => step !== IncentiveStep.LOGIN);

		this.mutations.setStep(next ?? fallback);

		if (!next) {
			this.onFinished?.();
		}
	}

	private trackAuthenticationResult(flow: SignInFlow, provider: AuthenticationProviderType): void {
		const track = flow === SignInFlow.REGISTERED ? trackIncentiveRegistration : trackIncentiveLogin;

		track(
			this.getters.portalType(),
			provider,
			this.getters.location(),
			this.getters.incentiveType(),
			undefined,
			this.getters.uniqueIdentifier(),
		);
	}

	private setPostAuthenticationStep(current: IncentiveStep): void {
		const optInStep = this.steps.find((step) => step === IncentiveStep.OPT_IN);
		if (optInStep) {
			this.mutations.setStep(optInStep);
			return;
		}

		const currentStepIndex = this.steps.indexOf(current);
		const nextStep = currentStepIndex >= 0
			? this.steps.slice(currentStepIndex + 1).find((step) => step !== IncentiveStep.LOGIN)
			: this.steps.find((step) => step !== IncentiveStep.REGISTRATION && step !== IncentiveStep.LOGIN);

		if (nextStep) {
			this.mutations.setStep(nextStep);
		}
	}

	private buildState(input?: IncentiveAuthenticationInput): IAuthenticationState {
		return {
			name: input?.provider === AuthenticationProviderType.DIRECT && 'name' in input ? input?.name ?? null : this.getters.name(),
			email: input?.provider === AuthenticationProviderType.DIRECT ? input.email : this.getters.email(),
			password: input?.provider === AuthenticationProviderType.DIRECT ? input.password : this.getters.password(),
			captcha: input?.provider === AuthenticationProviderType.DIRECT && 'captcha' in input ? input.captcha : this.captcha.captcha,
			googleIdToken: input?.provider === AuthenticationProviderType.GOOGLE ? input.googleIdToken : null,
			facebookAccessToken: input?.provider === AuthenticationProviderType.FACEBOOK ? input.facebookAccessToken : null,
			portalType: this.getters.portalType(),
			accessToken: this.getters.accessToken(),
			skipGlobalStoreWrite: true,
		};
	}
}
